//* Libraries imports
import { useQuery } from "@tanstack/react-query";

//* Config imports
import { API_URL, getAuthHeaders } from "@/config/api";

//* Utils imports
import { QUERY_KEYS } from "@/utils/query-client";
import { parseMongoData } from "@/utils/mongoUtils";

//* Types imports
import type { ISaleOutputDTO } from "@/schemas/sales";
import type { Client } from "@/schemas/client";
import type { IProduct } from "@/schemas/product";
import type { User } from "@/schemas/user";

const processSale = (sale: any): ISaleOutputDTO => ({
  ...sale,
  _id: sale._id?.$oid || sale._id || sale.id || "",
  clientId: parseMongoData<Client>(sale.clientId),
  productId: parseMongoData<IProduct>(sale.productId),
  responsibleId: parseMongoData<User>(sale.responsibleId),
});

async function getSale(id: string): Promise<ISaleOutputDTO> {
  const response = await fetch(`${API_URL}/sales/${id}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error("Failed to fetch sale");
  }

  const result = await response.json();
  return result.data ? processSale(result.data) : processSale(result);
}

export function useGetSale(id?: string, options?: { refetchInterval?: number }) {
  return useQuery<ISaleOutputDTO>({
    queryKey: [QUERY_KEYS.SALES.SINGLE, id],
    queryFn: () => getSale(id!),
    enabled: !!id,
    refetchInterval: options?.refetchInterval,
  });
}
